import { useQuery } from "@apollo/client";
import { BaseTable, Flexbox, IBaseTableProps } from "@sede-x/shell-ds-react-framework";
import { ColumnDef, getPaginationRowModel } from "@tanstack/react-table";
import { loader } from "graphql.macro";
import { useState } from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import LoadingPanel from "shared/components/LoadingPanel";
import { GqlResponse, TTicketRailcarRevision } from "ticketing/ticketing.types";

type Response = GqlResponse<TTicketRailcarRevision[], "railcarsOfTicketAtRevision">;

const query = loader("../../ticketing-graphql/railcarsOfTicketAtRevision.graphql");

const transformData = (ticketRevisionRailcars: TTicketRailcarRevision[]) => {
  return ticketRevisionRailcars?.map(trm => ({
    ...trm,
    lastModifiedDate: new Date(trm.lastModifiedDate + "Z")
  }));
};

type RevisionsTicketRailcarsProps = {
  ticketId: string;
  revisionNumber: string;
};

/***
 *
 */
const RevisionsTicketRailcars = ({
  ticketId,
  revisionNumber
}: RevisionsTicketRailcarsProps) => {
  const [revisions, setRevisions] = useState<TTicketRailcarRevision[]>();

  const { loading, error } = useQuery<Response>(query, {
    fetchPolicy: "no-cache",
    variables: { ticketId, revisionNumber },
    onCompleted: data => setRevisions(transformData(data.railcarsOfTicketAtRevision))
  });

  const columns: ColumnDef<TTicketRailcarRevision, string>[] = [
    {
      header: "Railcar Id",
      accessorKey: "railcarNumber"
    },
    {
      header: "Updated At",
      accessorKey: "lastModifiedDate",
      cell: props => props.row.original.lastModifiedDate.toLocaleString()
    },
    {
      header: "Updated By",
      accessorKey: "lastModifiedBy",
      cell: props =>
        [
          props.row.original.lastModifiedBy.lastName,
          props.row.original.lastModifiedBy.firstName
        ]
          .filter(Boolean)
          .join(", ")
    }
  ];

  const tableOptions: IBaseTableProps<TTicketRailcarRevision>["tableOptions"] = {
    defaultColumn: {
      minSize: 100,
      maxSize: 400
    },
    getPaginationRowModel: getPaginationRowModel(),
    columnResizeMode: "onChange",
    enableColumnResizing: true
  };

  return (
    <Flexbox
      flexDirection="column"
      style={{
        overflow: "auto"
      }}>
      {loading && <LoadingPanel />}
      {error && <ApolloErrorViewer error={error} />}
      <BaseTable
        data={revisions}
        columns={columns}
        noFooter={true}
        tableOptions={tableOptions}
        style={{ width: "100%", tableLayout: "fixed" }}
      />
    </Flexbox>
  );
};

export default RevisionsTicketRailcars;
